import FullCalendar from '@fullcalendar/react';
import { useRef, useEffect, useImperativeHandle, forwardRef, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import luxonPlugin from '@fullcalendar/luxon3';
import { Appointment } from '../../types/appointments';
import './ScheduleCalendar.css';
import type { BlackoutWindow } from '../docks/BlackoutList';
import { BlackoutDetailPopover } from './BlackoutDetailPopover';

import { Dialog, DialogPortal, DialogOverlay, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { resolveEffectiveTimeZone } from '../../utils/timezoneDisplay';

// ─── Constants ────────────────────────────────────────────────────────────────

const MIN_HOUR = 5;
const MAX_HOUR = 21;
const TOTAL_HOURS = MAX_HOUR - MIN_HOUR;

// ─── Shared Warehouse-Timezone Date Range Helpers ─────────────────────────────

export function resolveScheduleTimeZone(warehouseTimeZone?: string, refDate?: Date | string): string {
    return (
        resolveEffectiveTimeZone(warehouseTimeZone, refDate) ||
        warehouseTimeZone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'America/Los_Angeles'
    );
}

/**
 * Returns the current date in the warehouse timezone as YYYY-MM-DD.
 */
export function getWarehouseTodayIso(warehouseTimeZone?: string, nowMs: number = Date.now()): string {
    const zone = resolveScheduleTimeZone(warehouseTimeZone, new Date(nowMs));
    return DateTime.fromMillis(nowMs, { zone }).toISODate() || new Date(nowMs).toISOString().slice(0, 10);
}

/**
 * Given a YYYY-MM-DD date string, returns [startMs, endMs) for that calendar day in the warehouse timezone.
 */
export function getWarehouseDayRangeMs(dayIso: string, warehouseTimeZone?: string): { startMs: number; endMs: number; dt: DateTime } {
    const zone = resolveScheduleTimeZone(warehouseTimeZone, dayIso);
    const dt = DateTime.fromISO(dayIso, { zone }).startOf('day');
    return {
        startMs: dt.toMillis(),
        endMs: dt.plus({ days: 1 }).toMillis(),
        dt,
    };
}

/**
 * Returns the Monday (YYYY-MM-DD) of the week containing `refDayIso` (or today in warehouse timezone).
 * Luxon weekday: 1 = Monday ... 7 = Sunday.
 */
export function getWarehouseWeekMondayIso(warehouseTimeZone?: string, refDayIso?: string, nowMs: number = Date.now()): string {
    const baseIso = refDayIso || getWarehouseTodayIso(warehouseTimeZone, nowMs);
    const zone = resolveScheduleTimeZone(warehouseTimeZone, baseIso);
    const dt = DateTime.fromISO(baseIso, { zone }).startOf('day');
    const diffToMonday = dt.weekday - 1; // 0 for Monday, 6 for Sunday
    const monday = dt.minus({ days: diffToMonday });
    return monday.toISODate() || baseIso;
}

/**
 * Given a week's Monday (YYYY-MM-DD), returns [startMs, endMs) for the 7-day Monday..Sunday window in warehouse timezone.
 */
export function getWarehouseWeekRangeMs(mondayIso: string, warehouseTimeZone?: string): { startMs: number; endMs: number; mondayDt: DateTime } {
    const zone = resolveScheduleTimeZone(warehouseTimeZone, mondayIso);
    const mondayDt = DateTime.fromISO(mondayIso, { zone }).startOf('day');
    return {
        startMs: mondayDt.toMillis(),
        endMs: mondayDt.plus({ days: 7 }).toMillis(),
        mondayDt,
    };
}

/**
 * Returns the first day of the month (YYYY-MM-01) containing `refDayIso` (or today in warehouse timezone).
 */
export function getWarehouseMonthStartIso(warehouseTimeZone?: string, refDayIso?: string, nowMs: number = Date.now()): string {
    const baseIso = refDayIso || getWarehouseTodayIso(warehouseTimeZone, nowMs);
    const zone = resolveScheduleTimeZone(warehouseTimeZone, baseIso);
    const dt = DateTime.fromISO(baseIso, { zone }).startOf('month');
    return dt.toISODate() || `${baseIso.slice(0, 7)}-01`;
}

/**
 * Given a month start (YYYY-MM-01), returns [startMs, endMs) for that calendar month in warehouse timezone.
 */
export function getWarehouseMonthRangeMs(monthStartIso: string, warehouseTimeZone?: string): { startMs: number; endMs: number; monthStartDt: DateTime } {
    const zone = resolveScheduleTimeZone(warehouseTimeZone, monthStartIso);
    const monthStartDt = DateTime.fromISO(monthStartIso, { zone }).startOf('month');
    return {
        startMs: monthStartDt.toMillis(),
        endMs: monthStartDt.plus({ months: 1 }).toMillis(),
        monthStartDt,
    };
}

/**
 * Computes consistent non-cancelled appointment counts for the active day, Mon-Sun week, and calendar month.
 */
export function computeScheduleScopeCounts(
    appointments: Appointment[],
    options: {
        warehouseTimeZone?: string;
        selectedDayIso: string;
        selectedWeekMondayIso: string;
        selectedMonthStartIso: string;
    }
): { today: number; days7: number; month: number } {
    const { warehouseTimeZone, selectedDayIso, selectedWeekMondayIso, selectedMonthStartIso } = options;
    const dayRange = getWarehouseDayRangeMs(selectedDayIso, warehouseTimeZone);
    const weekRange = getWarehouseWeekRangeMs(selectedWeekMondayIso, warehouseTimeZone);
    const monthRange = getWarehouseMonthRangeMs(selectedMonthStartIso, warehouseTimeZone);

    let today = 0;
    let days7 = 0;
    let month = 0;

    for (const appt of appointments) {
        if (normalizeStatus(appt.status) === 'cancelled') continue;
        const t = new Date(appt.startAt).getTime();
        if (Number.isNaN(t)) continue;
        if (t >= dayRange.startMs && t < dayRange.endMs) today++;
        if (t >= weekRange.startMs && t < weekRange.endMs) days7++;
        if (t >= monthRange.startMs && t < monthRange.endMs) month++;
    }

    return { today, days7, month };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatHour(h: number): string {
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${hr}${ampm}`;
}

function formatEventTime(date: Date, warehouseTimeZone: string): string {
    try {
        const effectiveTz = resolveEffectiveTimeZone(warehouseTimeZone, date) || warehouseTimeZone;
        return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: effectiveTz });
    } catch {
        return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
}

function getDockLabel(appt: Appointment): string | null {
    const dockId = appt.dockId || appt.dock_id;
    if (!dockId) return null;
    const str = String(dockId);
    if (str.length <= 6) return `Dock ${str}`;
    return 'Dock \u2713';
}

function normalizeStatus(s: string | undefined): string {
    return (s ?? '').toLowerCase();
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type CalendarViewType = 'timeGridDay' | 'timeGridWeek' | 'dayGridMonth';

export interface ScheduleCalendarHandle {
    changeView: (view: CalendarViewType) => void;
}

export interface ScheduleCalendarProps {
    appointments: Appointment[];
    blackouts?: BlackoutWindow[];
    warehouseTimeZone?: string;
    onEventClick: (appointment: Appointment) => void;
    onDateSelect?: (start: Date, end: Date) => void;
    onMonthChanged?: (visibleStart: Date, visibleEnd: Date) => void;
    dockCount?: number;
    initialView?: CalendarViewType;
    docks?: { id: string; dockNumber?: string; name?: string; code?: string }[];
    selectedDayIso?: string;
    onDayChange?: (dayIso: string) => void;
    selectedWeekMondayIso?: string;
    onWeekChange?: (mondayIso: string) => void;
    selectedMonthStartIso?: string;
    onMonthChange?: (monthStartIso: string) => void;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TODAY VIEW — Vertical Dock Timeline Board
// rows = time, columns = docks
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface DockTimelineBoardProps {
    warehouseTimeZone: string;
    appointments: Appointment[];
    docks: { id: string; dockNumber?: string; name?: string; code?: string }[];
    dockCount: number;
    onEventClick: (appt: Appointment) => void;
    blackouts?: BlackoutWindow[];
    selectedDayIso: string;
    onDayChange?: (dayIso: string) => void;
}

function DockTimelineBoard({
    appointments,
    docks,
    dockCount,
    onEventClick,
    blackouts,
    warehouseTimeZone,
    selectedDayIso,
    onDayChange,
}: DockTimelineBoardProps) {
    const [popoverBlackout, setPopoverBlackout] = useState<{ blackout: BlackoutWindow; rect: DOMRect } | null>(null);
    const now = new Date();
    const effectiveZone = resolveScheduleTimeZone(warehouseTimeZone, selectedDayIso);
    const nowInZone = DateTime.fromJSDate(now, { zone: effectiveZone });
    const currentHour = nowInZone.hour;
    const todayIso = getWarehouseTodayIso(warehouseTimeZone);
    const { startMs: startOfDayMs, endMs: endOfDayMs, dt: selectedDayDt } = useMemo(
        () => getWarehouseDayRangeMs(selectedDayIso, warehouseTimeZone),
        [selectedDayIso, warehouseTimeZone]
    );

    const todayAppts = useMemo(
        () =>
            appointments.filter(a => {
                const t = new Date(a.startAt).getTime();
                return t >= startOfDayMs && t < endOfDayMs && normalizeStatus(a.status) !== 'cancelled';
            }),
        [appointments, startOfDayMs, endOfDayMs]
    );

    const { dockLanes, unassigned } = useMemo(() => {
        const assigned = new Map<string, Appointment[]>();
        const unassignedList: Appointment[] = [];
        const knownDockIds = new Set(docks.map(d => String(d.id)));
        todayAppts.forEach(a => {
            const dockId = a.dockId || a.dock_id;
            if (dockId && knownDockIds.has(String(dockId))) {
                const list = assigned.get(String(dockId)) || [];
                list.push(a);
                assigned.set(String(dockId), list);
            } else {
                unassignedList.push(a);
            }
        });

        const getExtractableLabel = (d: { id: string; dockNumber?: string; name?: string; code?: string }) => {
            if (d.dockNumber) return String(d.dockNumber);
            if (d.name) return String(d.name);
            if (d.code) return String(d.code);
            const idStr = String(d.id);
            if (/^(d\s*\d|dock)/i.test(idStr)) return idStr;
            return '';
        };

        const getFallbackLabel = (d: { id: string; dockNumber?: string; name?: string; code?: string }) => {
            if (d.dockNumber) return String(d.dockNumber);
            if (d.name) return String(d.name);
            if (d.code) return String(d.code);
            return String(d.id);
        };

        const sortedDocks = [...docks].sort((a, b) => {
            const extA = getExtractableLabel(a);
            const extB = getExtractableLabel(b);

            const numA = extA.match(/\d+/);
            const numB = extB.match(/\d+/);

            if (numA && numB) {
                const valA = parseInt(numA[0], 10);
                const valB = parseInt(numB[0], 10);
                if (valA !== valB) return valA - valB;
            } else if (numA) {
                return -1;
            } else if (numB) {
                return 1;
            }

            return getFallbackLabel(a).localeCompare(getFallbackLabel(b));
        });

        const lanes = sortedDocks.map(dock => ({
            id: dock.id,
            label: dock.dockNumber ? `Dock ${dock.dockNumber}` : (dock.name || dock.code || `Dock ${dock.id.replace('D-', '')}`),
            appointments: (assigned.get(dock.id) || []).sort(
                (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
            ),
        }));
        return { dockLanes: lanes, unassigned: unassignedList };
    }, [todayAppts, docks]);

    const nextArrivals = useMemo(
        () =>
            todayAppts
                .filter(a => {
                    const s = normalizeStatus(a.status);
                    return s !== 'completed' && s !== 'checked_in' && s !== 'in_progress' && new Date(a.startAt) > now;
                })
                .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime())
                .slice(0, 4),
        [todayAppts]
    );

    const isOverCapacity = dockCount > 0 && todayAppts.length > dockCount;
    const ratio = dockCount > 0 ? todayAppts.length / dockCount : 0;

    const nowMinutes = nowInZone.hour * 60 + nowInZone.minute;
    const minMinutes = MIN_HOUR * 60;
    const maxMinutes = MAX_HOUR * 60;
    const nowPct = Math.max(0, Math.min(100, ((nowMinutes - minMinutes) / (maxMinutes - minMinutes)) * 100));

    const hours = Array.from({ length: TOTAL_HOURS + 1 }, (_, i) => MIN_HOUR + i);

    function getBlockClass(appt: Appointment): string {
        const s = normalizeStatus(appt.status);
        if (s === 'completed') return 'dtb-block-completed';
        if (s === 'in_progress') return 'dtb-block-active';
        if (s === 'checked_in') return 'dtb-block-checkedin';
        const startMs = new Date(appt.startAt).getTime();
        if (startMs < now.getTime()) return 'dtb-block-late';
        return 'dtb-block-scheduled';
    }

    function getVerticalPosition(appt: Appointment) {
        const startDt = DateTime.fromMillis(new Date(appt.startAt).getTime(), { zone: effectiveZone });
        const endMs = appt.endAt ? new Date(appt.endAt).getTime() : new Date(appt.startAt).getTime() + 60 * 60 * 1000;
        const endDt = DateTime.fromMillis(endMs, { zone: effectiveZone });
        const startMin = startDt.hour * 60 + startDt.minute;
        const endMin = endDt.hour * 60 + endDt.minute;
        const clampStart = Math.max(startMin, minMinutes);
        const clampEnd = Math.min(Math.max(endMin, startMin + 30), maxMinutes);
        if (clampStart >= clampEnd) {
            return { top: 0, height: 4 };
        }
        const top = ((clampStart - minMinutes) / (maxMinutes - minMinutes)) * 100;
        const height = Math.max(2, ((clampEnd - clampStart) / (maxMinutes - minMinutes)) * 100);
        return { top, height };
    }

    function getBlackoutVerticalPosition(b: BlackoutWindow) {
        const startDt = DateTime.fromMillis(new Date(b.startAt).getTime(), { zone: effectiveZone });
        const endDt = DateTime.fromMillis(new Date(b.endAt).getTime(), { zone: effectiveZone });
        const startMin = startDt.hour * 60 + startDt.minute;
        const endMin = endDt.hour * 60 + endDt.minute;
        const clampStart = Math.max(startMin, minMinutes);
        const clampEnd = Math.min(endMin, maxMinutes);
        if (clampStart >= clampEnd) return null;
        const top = ((clampStart - minMinutes) / (maxMinutes - minMinutes)) * 100;
        const height = Math.max(2, ((clampEnd - clampStart) / (maxMinutes - minMinutes)) * 100);
        return { top, height };
    }

    const pinnedNext = nextArrivals.length > 0 ? nextArrivals[0] : null;

    // Build columns: docks + optional unassigned
    const columns = [
        ...dockLanes.map(l => ({ id: l.id, label: l.label, appointments: l.appointments, isUnassigned: false })),
        ...(unassigned.length > 0 || dockLanes.length === 0
            ? [{ id: '_unassigned', label: 'Unassigned', appointments: unassigned, isUnassigned: true }]
            : []),
    ];

    const handleShiftDay = (deltaDays: number) => {
        if (!onDayChange) return;
        const nextIso = selectedDayDt.plus({ days: deltaDays }).toISODate();
        if (nextIso) onDayChange(nextIso);
    };

    return (
        <div className="dtb-container">
            <div className="dtb-main">
                {/* Day navigation header */}
                <div className="flex items-center justify-between gap-2 mb-3 px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/70 flex-wrap">
                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            aria-label="Previous day"
                            onClick={() => handleShiftDay(-1)}
                            className="px-2.5 py-1 text-xs font-semibold rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                            {'\u2190'} Prev
                        </button>
                        <button
                            type="button"
                            aria-label="Next day"
                            onClick={() => handleShiftDay(1)}
                            className="px-2.5 py-1 text-xs font-semibold rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                            Next {'\u2192'}
                        </button>
                        {selectedDayIso !== todayIso && (
                            <button
                                type="button"
                                aria-label="Go to today"
                                onClick={() => onDayChange && onDayChange(todayIso)}
                                className="px-2.5 py-1 text-xs font-semibold rounded bg-blue-600 text-white hover:bg-blue-700"
                            >
                                Today
                            </button>
                        )}
                    </div>
                    <div className="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200" data-testid="schedule-day-label">
                        {selectedDayDt.toFormat('ccc, LLL d, yyyy')} ({todayAppts.length} scheduled)
                    </div>
                </div>

                {/* Pinned next-appointment */}
                {pinnedNext && (
                    <div className="dtb-pinned-next" onClick={() => onEventClick(pinnedNext)}>
                        <span className="dtb-pinned-label">NEXT {'\u2192'}</span>
                        <span className="dtb-pinned-time">{formatEventTime(new Date(pinnedNext.startAt), warehouseTimeZone || 'America/Los_Angeles')}</span>
                        {getDockLabel(pinnedNext) && <span className="dtb-pinned-dock">{getDockLabel(pinnedNext)}</span>}
                        <span className="dtb-pinned-carrier">{pinnedNext.carrierName || 'Unknown'}</span>
                    </div>
                )}

                {/* Capacity banner */}
                {isOverCapacity && (
                    <div className="dtb-capacity-alert">
                        {'\u26a0'} Over capacity: {todayAppts.length} / {dockCount} docks ({Math.round(ratio * 100)}%)
                    </div>
                )}

                {/* Vertical grid */}
                <div className="dtb-vgrid">
                    {/* Dock column headers */}
                    <div className="dtb-vgrid-header">
                        <div className="dtb-vgrid-time-spacer" />
                        {columns.map(col => (
                            <div key={col.id} className={`dtb-vgrid-dock-header ${col.isUnassigned ? 'dtb-header-warn' : ''}`}>
                                <span className="dtb-vgrid-dock-name">{col.label}</span>
                                <span className={`dtb-dock-count ${col.isUnassigned ? 'dtb-count-warn' : ''}`}>{col.appointments.length}</span>
                            </div>
                        ))}
                    </div>

                    {/* Grid body */}
                    <div className="dtb-vgrid-body">
                        {/* Time axis */}
                        <div className="dtb-vgrid-time-axis">
                            {hours.map(h => {
                                const topPct = ((h - MIN_HOUR) / TOTAL_HOURS) * 100;
                                return (
                                    <div
                                        key={h}
                                        className={`dtb-vgrid-time-label ${h === currentHour ? 'dtb-vtime-current' : ''}`}
                                        style={{ top: `${topPct}%` }}
                                    >
                                        {formatHour(h)}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Dock columns */}
                        {columns.map(col => (
                            <div key={col.id} className={`dtb-vgrid-col ${col.isUnassigned ? 'dtb-vcol-warn' : ''}`}>
                                {/* Hour grid lines */}
                                {hours.map(h => {
                                    const topPct = ((h - MIN_HOUR) / TOTAL_HOURS) * 100;
                                    return (
                                        <div
                                            key={h}
                                            className={`dtb-vgrid-hline ${h === currentHour ? 'dtb-vhline-current' : ''}`}
                                            style={{ top: `${topPct}%` }}
                                        />
                                    );
                                })}

                                {/* Now line */}
                                <div className="dtb-vnow-line" style={{ top: `${nowPct}%` }} />

                                {/* V1.3C: Blackout blocks — behind appointments (z-index: 1) */}
                                {!col.isUnassigned && (blackouts || []).filter(b => b.dockId === col.id || b.isWarehouseWide).map(b => {
                                    const pos = getBlackoutVerticalPosition(b);
                                    if (!pos) return null;
                                    return (
                                        <div
                                            key={`bo-${b.id}`}
                                            className="dtb-blackout-vblock"
                                            style={{ top: `${pos.top}%`, height: `${pos.height}%` }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                setPopoverBlackout({ blackout: b, rect });
                                            }}
                                            title={`Blocked: ${b.reason}`}
                                        >
                                            {b.isWarehouseWide && <span className="blackout-badge-alldocks">ALL DOCKS</span>}
                                            <span className="blackout-block-label">{b.reason}</span>
                                        </div>
                                    );
                                })}

                                {/* Appointment blocks */}
                                {col.appointments.map(appt => {
                                    const pos = getVerticalPosition(appt);
                                    if (!pos) return null;
                                    const timeStr = formatEventTime(new Date(appt.startAt), warehouseTimeZone || 'America/Los_Angeles');
                                    const status = normalizeStatus(appt.status);
                                    const dockLabel = getDockLabel(appt);

                                    return (
                                        <div
                                            key={appt.id}
                                            className={`dtb-vblock ${getBlockClass(appt)}`}
                                            style={{ top: `${pos.top}%`, height: `${pos.height}%` }}
                                            onClick={() => onEventClick(appt)}
                                            title={`${timeStr} \u2014 ${appt.carrierName || 'Unknown'}\n${dockLabel || ''}\n${status}`}
                                        >
                                            <span className="dtb-vblock-time">{timeStr}</span>
                                            <span className="dtb-vblock-carrier">{appt.carrierName || 'Unknown'}</span>
                                        </div>
                                    );
                                })}

                                {col.appointments.length === 0 && (
                                    <span className="dtb-idle">idle</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Legend */}
                <div className="dtb-legend">
                    <span className="dtb-legend-item"><span className="dtb-dot dtb-dot-scheduled" />Scheduled</span>
                    <span className="dtb-legend-item"><span className="dtb-dot dtb-dot-checkedin" />Checked In</span>
                    <span className="dtb-legend-item"><span className="dtb-dot dtb-dot-active" />Unloading</span>
                    <span className="dtb-legend-item"><span className="dtb-dot dtb-dot-completed" />Complete</span>
                    <span className="dtb-legend-item"><span className="dtb-dot dtb-dot-late" />Late</span>
                    <span className="dtb-legend-item"><span className="dtb-dot dtb-dot-blocked" />Blocked</span>
                </div>
            </div>

            {/* Control rail */}
            <div className="dtb-panel">
                <div className="dtb-panel-section">
                    <div className="dtb-panel-title">Next Arrivals</div>
                    {nextArrivals.length === 0 ? (
                        <div className="dtb-panel-empty">No upcoming arrivals</div>
                    ) : (
                        nextArrivals.map(a => (
                            <div key={a.id} className="dtb-panel-card" onClick={() => onEventClick(a)}>
                                <span className="dtb-panel-time">{formatEventTime(new Date(a.startAt), warehouseTimeZone)}</span>
                                <span className="dtb-panel-carrier">{a.carrierName || 'Unknown'}</span>
                                {getDockLabel(a) && <span className="dtb-panel-dock">{getDockLabel(a)}</span>}
                            </div>
                        ))
                    )}
                </div>
                <div className="dtb-panel-section">
                    <div className="dtb-panel-title">Unassigned</div>
                    {unassigned.length === 0 ? (
                        <div className="dtb-panel-empty">All assigned</div>
                    ) : (
                        unassigned.slice(0, 4).map(a => (
                            <div key={a.id} className="dtb-panel-card dtb-panel-card-warn" onClick={() => onEventClick(a)}>
                                <span className="dtb-panel-time">{formatEventTime(new Date(a.startAt), warehouseTimeZone)}</span>
                                <span className="dtb-panel-carrier">{a.carrierName || 'Unknown'}</span>
                            </div>
                        ))
                    )}
                </div>
                <div className="dtb-panel-section">
                    <div className="dtb-panel-title">Capacity</div>
                    <div className={`dtb-capacity-badge ${ratio > 1 ? 'dtb-cap-over' : ratio >= 0.7 ? 'dtb-cap-warn' : 'dtb-cap-ok'}`}>
                        <span className="dtb-cap-num">{todayAppts.length}</span>
                        <span className="dtb-cap-div">/</span>
                        <span className="dtb-cap-den">{dockCount}</span>
                        <span className="dtb-cap-pct">{Math.round(ratio * 100)}%</span>
                    </div>
                </div>
            </div>

            {popoverBlackout && (
                <BlackoutDetailPopover
                    blackout={popoverBlackout.blackout}
                    anchorRect={popoverBlackout.rect}
                    onClose={() => setPopoverBlackout(null)}
                />
            )}
        </div>
    );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 7-DAY VIEW — Compressed Day-Column Planner
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface WeekPlannerBoardProps {
    warehouseTimeZone: string;
    appointments: Appointment[];
    dockCount: number;
    onEventClick: (appt: Appointment) => void;
    selectedWeekMondayIso: string;
    onWeekChange?: (mondayIso: string) => void;
}

function WeekPlannerBoard({
    appointments,
    dockCount,
    onEventClick,
    warehouseTimeZone,
    selectedWeekMondayIso,
    onWeekChange,
}: WeekPlannerBoardProps) {
    const [expandedDay, setExpandedDay] = useState<string | null>(null);
    const MAX_VISIBLE = 3;

    const todayIso = getWarehouseTodayIso(warehouseTimeZone);
    const currentWeekMondayIso = getWarehouseWeekMondayIso(warehouseTimeZone);
    const { mondayDt } = useMemo(
        () => getWarehouseWeekRangeMs(selectedWeekMondayIso, warehouseTimeZone),
        [selectedWeekMondayIso, warehouseTimeZone]
    );

    // Group appointments by each of the 7 Mon-Sun days in warehouse timezone
    const dayData = useMemo(() => {
        return Array.from({ length: 7 }, (_, i) => {
            const dayDt = mondayDt.plus({ days: i }).startOf('day');
            const dayIso = dayDt.toISODate() || '';
            const dayStartMs = dayDt.toMillis();
            const dayEndMs = dayDt.plus({ days: 1 }).toMillis();

            const dayAppts = appointments
                .filter(a => {
                    const t = new Date(a.startAt).getTime();
                    return t >= dayStartMs && t < dayEndMs && normalizeStatus(a.status) !== 'cancelled';
                })
                .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

            const count = dayAppts.length;
            const ratio = dockCount > 0 ? count / dockCount : 0;
            const level = ratio > 1 ? 'over' : ratio >= 0.7 ? 'warn' : 'ok';
            const isToday = dayIso === todayIso;

            return {
                dayDt,
                dayName: dayDt.toFormat('ccc'),
                dayNum: dayDt.day,
                dayAppts,
                count,
                ratio,
                level,
                isToday,
                key: dayIso,
            };
        });
    }, [appointments, dockCount, mondayDt, todayIso]);

    const totalWeekCount = useMemo(
        () => dayData.reduce((sum, d) => sum + d.count, 0),
        [dayData]
    );

    const sundayDt = mondayDt.plus({ days: 6 });
    const weekLabel = `${mondayDt.toFormat('LLL d')} \u2013 ${sundayDt.toFormat('LLL d, yyyy')}`;

    const handleShiftWeek = (deltaWeeks: number) => {
        if (!onWeekChange) return;
        const nextMondayIso = mondayDt.plus({ days: deltaWeeks * 7 }).toISODate();
        if (nextMondayIso) onWeekChange(nextMondayIso);
    };

    const barColor = (level: string) =>
        level === 'over' ? '#ef4444' : level === 'warn' ? '#f59e0b' : '#94a3b8';

    return (
        <div>
            {/* Week navigation header */}
            <div className="flex items-center justify-between gap-2 mb-3 px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm flex-wrap">
                <div className="flex items-center gap-1.5">
                    <button
                        type="button"
                        aria-label="Previous week"
                        onClick={() => handleShiftWeek(-1)}
                        className="px-2.5 py-1 text-xs font-semibold rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                        {'\u2190'} Prev Week
                    </button>
                    <button
                        type="button"
                        aria-label="Next week"
                        onClick={() => handleShiftWeek(1)}
                        className="px-2.5 py-1 text-xs font-semibold rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                        Next Week {'\u2192'}
                    </button>
                    {selectedWeekMondayIso !== currentWeekMondayIso && (
                        <button
                            type="button"
                            aria-label="Go to current week"
                            onClick={() => onWeekChange && onWeekChange(currentWeekMondayIso)}
                            className="px-2.5 py-1 text-xs font-semibold rounded bg-blue-600 text-white hover:bg-blue-700"
                        >
                            This Week
                        </button>
                    )}
                </div>
                <div className="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200" data-testid="schedule-week-label">
                    {weekLabel} ({totalWeekCount} scheduled)
                </div>
            </div>

            <div className="wpb-container">
                {dayData.map(day => {
                    const isExpanded = expandedDay === day.key;
                    const visibleAppts = isExpanded ? day.dayAppts : day.dayAppts.slice(0, MAX_VISIBLE);
                    const overflow = day.count - MAX_VISIBLE;

                    return (
                        <div
                            key={day.key}
                            className={`wpb-day-col ${day.isToday ? 'wpb-day-today' : ''} ${day.level === 'over' ? 'wpb-day-over' : day.level === 'warn' ? 'wpb-day-warn' : ''}`}
                        >
                            {/* Day header */}
                            <div className="wpb-day-header">
                                <div className="wpb-day-name">
                                    {day.dayName}
                                </div>
                                <div className={`wpb-day-num ${day.isToday ? 'wpb-num-today' : ''}`}>
                                    {day.dayNum}
                                </div>
                                {dockCount > 0 && day.count > 0 && (
                                    <div className={`wpb-cap-badge wpb-cap-${day.level}`}>
                                        {day.count} / {dockCount}
                                    </div>
                                )}
                            </div>

                            {/* Capacity bar */}
                            {dockCount > 0 && day.count > 0 && (
                                <div className="wpb-bar-track">
                                    <div
                                        className="wpb-bar-fill"
                                        style={{
                                            width: `${Math.min(day.ratio * 100, 100)}%`,
                                            backgroundColor: barColor(day.level),
                                        }}
                                    />
                                </div>
                            )}

                            {/* Appointment list */}
                            <div className="wpb-appt-list">
                                {day.count === 0 && (
                                    <div className="wpb-empty">No appointments</div>
                                )}
                                {visibleAppts.map(appt => {
                                    const status = normalizeStatus(appt.status);
                                    const isLate = status !== 'completed' && status !== 'cancelled'
                                        && status !== 'checked_in' && status !== 'in_progress'
                                        && new Date() > new Date(appt.startAt);
                                    const statusClass = status === 'completed' ? 'wpb-appt-completed'
                                        : status === 'in_progress' ? 'wpb-appt-active'
                                        : status === 'checked_in' ? 'wpb-appt-checkedin'
                                        : isLate ? 'wpb-appt-late'
                                        : 'wpb-appt-scheduled';

                                    return (
                                        <div
                                            key={appt.id}
                                            className={`wpb-appt-row ${statusClass}`}
                                            onClick={() => onEventClick(appt)}
                                            title={`${appt.carrierName || 'Unknown'}\n${getDockLabel(appt) || ''}\n${status}`}
                                        >
                                            {isLate && <span className="wpb-late-dot" />}
                                            <span className="wpb-appt-time">{formatEventTime(new Date(appt.startAt), warehouseTimeZone || 'America/Los_Angeles')}</span>
                                            <span className="wpb-appt-carrier">{appt.carrierName || 'Unknown'}</span>
                                        </div>
                                    );
                                })}

                                {/* +N more */}
                                {!isExpanded && overflow > 0 && (
                                    <button
                                        className="wpb-more-btn"
                                        onClick={() => setExpandedDay(day.key)}
                                    >
                                        +{overflow} more
                                    </button>
                                )}
                                {isExpanded && overflow > 0 && (
                                    <button
                                        className="wpb-more-btn"
                                        onClick={() => setExpandedDay(null)}
                                    >
                                        Show less
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export const ScheduleCalendar = forwardRef<ScheduleCalendarHandle, ScheduleCalendarProps>(
    function ScheduleCalendar(
        {
            appointments,
            onEventClick,
            dockCount,
            initialView = 'dayGridMonth',
            docks = [],
            blackouts,
            warehouseTimeZone,
            selectedDayIso,
            onDayChange,
            selectedWeekMondayIso,
            onWeekChange,
            selectedMonthStartIso,
            onMonthChange,
        },
        ref
    ) {
        const calendarRef = useRef<FullCalendar>(null);
        const [selectedDateForModal, setSelectedDateForModal] = useState<string | null>(null);

        const effectiveZone = useMemo(
            () => resolveScheduleTimeZone(warehouseTimeZone),
            [warehouseTimeZone]
        );

        const activeDayIso = selectedDayIso || getWarehouseTodayIso(warehouseTimeZone);
        const activeWeekMondayIso = selectedWeekMondayIso || getWarehouseWeekMondayIso(warehouseTimeZone);
        const activeMonthStartIso = selectedMonthStartIso || getWarehouseMonthStartIso(warehouseTimeZone);

        const dayApptCounts = useMemo(() => {
            const counts = new Map<string, number>();
            appointments.forEach(a => {
                if (normalizeStatus(a.status) === 'cancelled') return;
                const dt = DateTime.fromMillis(new Date(a.startAt).getTime(), { zone: effectiveZone });
                if (!dt.isValid) return;
                const iso = dt.toISODate();
                if (!iso) return;
                counts.set(iso, (counts.get(iso) || 0) + 1);
            });
            return counts;
        }, [appointments, effectiveZone]);

        useImperativeHandle(ref, () => ({
            changeView: (view: CalendarViewType) => {
                calendarRef.current?.getApi().changeView(view);
            },
        }));

        useEffect(() => {
            const api = calendarRef.current?.getApi();
            if (api && api.view.type !== initialView) {
                api.changeView(initialView);
            }
        }, [initialView]);

        // ─── TODAY: Dock Timeline Board ───
        if (initialView === 'timeGridDay') {
            return (
                <DockTimelineBoard
                    warehouseTimeZone={effectiveZone}
                    appointments={appointments}
                    docks={docks}
                    dockCount={dockCount || 0}
                    onEventClick={onEventClick}
                    blackouts={blackouts}
                    selectedDayIso={activeDayIso}
                    onDayChange={onDayChange}
                />
            );
        }

        // ─── 7-DAY: Compressed Day-Column Planner ───
        if (initialView === 'timeGridWeek') {
            return (
                <WeekPlannerBoard
                    warehouseTimeZone={effectiveZone}
                    appointments={appointments}
                    dockCount={dockCount || 0}
                    onEventClick={onEventClick}
                    selectedWeekMondayIso={activeWeekMondayIso}
                    onWeekChange={onWeekChange}
                />
            );
        }

        // ─── MONTH: FullCalendar ───
        const events = appointments
            .filter(appt => normalizeStatus(appt.status) !== 'cancelled')
            .map(appt => {
                const start = new Date(appt.startAt);
                const end = appt.endAt ? new Date(appt.endAt) : new Date(start.getTime() + 60 * 60 * 1000);
                let className = 'fc-event-default';
                const status = normalizeStatus(appt.status);

                if (status === 'in_progress') className = 'fc-event-inprogress';
                else if (status === 'checked_in') className = 'fc-event-checkedin';
                else if (status === 'completed') className = 'fc-event-completed';
                else if (status === 'approved') className = 'fc-event-approved';

                const isLate = status !== 'completed' && status !== 'cancelled'
                    && status !== 'checked_in' && status !== 'in_progress'
                    && new Date() > start;
                if (isLate) className += ' fc-event-late';

                return {
                    id: appt.id,
                    title: appt.carrierName || 'Unknown Carrier',
                    start,
                    end,
                    extendedProps: { appointment: appt, isLate },
                    className,
                };
            });

        // Resolve a FullCalendar cell Date (UTC marker when timeZone is named) to YYYY-MM-DD
        const resolveCellIso = (cellDate: Date): string => {
            const y = cellDate.getUTCFullYear();
            const m = String(cellDate.getUTCMonth() + 1).padStart(2, '0');
            const d = String(cellDate.getUTCDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        };

        return (
            <div className="schedule-calendar-container bg-white dark:bg-slate-900 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-800">
                <FullCalendar
                    timeZone={effectiveZone}
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, luxonPlugin]}
                    initialView={initialView}
                    initialDate={activeMonthStartIso}
                    headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: ''
                    }}
                    datesSet={(dateInfo) => {
                        if (!onMonthChange) return;
                        const y = dateInfo.view.currentStart.getUTCFullYear();
                        const m = String(dateInfo.view.currentStart.getUTCMonth() + 1).padStart(2, '0');
                        const nextMonthIso = `${y}-${m}-01`;
                        if (nextMonthIso !== activeMonthStartIso) {
                            onMonthChange(nextMonthIso);
                        }
                    }}
                    nowIndicator={true}
                    scrollTime="06:00:00"
                    slotMinTime="05:00:00"
                    slotMaxTime="21:00:00"
                    allDaySlot={false}
                    slotDuration="00:30:00"
                    fixedWeekCount={false}
                    dayMaxEvents={1}
                    moreLinkClick="popover"
                    events={events}
                    dateClick={(arg) => {
                        if (calendarRef.current?.getApi().view.type === 'dayGridMonth') {
                            setSelectedDateForModal(arg.dateStr);
                        }
                    }}
                    eventClick={(info) => { if (onEventClick) onEventClick(info.event.extendedProps.appointment); }}
                    eventClassNames={(_arg) => {
                        if (calendarRef.current?.getApi().view.type === 'dayGridMonth') {
                            return ['fc-month-event-hidden'];
                        }
                        return [];
                    }}
                    eventContent={(arg) => {
                        const appt: Appointment = arg.event.extendedProps.appointment;
                        const lateFlag: boolean = arg.event.extendedProps.isLate;
                        const timeStr = formatEventTime(arg.event.start!, effectiveZone);
                        const dockLabel = getDockLabel(appt);

                        const tooltipLines = [
                            appt.carrierName || 'Unknown',
                            dockLabel || '',
                            normalizeStatus(appt.status),
                        ].filter(Boolean).join('\n');

                        const viewType = calendarRef.current?.getApi().view.type;

                        if (viewType === 'dayGridMonth') {
                            return (
                                <div className="fc-month-event" title={tooltipLines}>
                                    {lateFlag && <span className="fc-late-icon">{'\u26a0'} </span>}
                                    <span className="fc-month-time">{timeStr}</span>
                                    {dockLabel && <span className="fc-month-dock"> {'\u00b7'} {dockLabel}</span>}
                                </div>
                            );
                        }

                        // 7-Day view: card with carrier
                        return (
                            <div className="fc-week-event" title={tooltipLines}>
                                {lateFlag && <span className="fc-late-icon">{'\u26a0'} </span>}
                                <span className="fc-week-time">{timeStr}</span>
                                <span className="fc-week-carrier">{appt.carrierName || 'Unknown'}</span>
                            </div>
                        );
                    }}
                    height="75vh"
                    slotLaneClassNames={(arg) => {
                        if (arg.date) {
                            const slotHour = arg.date.getHours();
                            if (slotHour === new Date().getHours()) return ['fc-slot-current-hour'];
                        }
                        return [];
                    }}
                    dayHeaderClassNames={(arg) => {
                        if (!dockCount || dockCount <= 0) return [];
                        const iso = resolveCellIso(arg.date);
                        const count = dayApptCounts.get(iso) || 0;
                        const r = count / dockCount;
                        if (r > 1) return ['fc-day-overloaded'];
                        if (r >= 0.7) return ['fc-day-warn'];
                        return [];
                    }}
                    dayHeaderContent={(arg) => {
                        const view = calendarRef.current?.getApi().view.type;
                        if (view !== 'timeGridWeek') return arg.text;

                        const dayName = arg.date.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
                        const dayNum = arg.date.getUTCDate();
                        const iso = resolveCellIso(arg.date);
                        const isToday = iso === activeDayIso;

                        const dayCount = dayApptCounts.get(iso) || 0;
                        const hasDocks = dockCount && dockCount > 0;
                        const capRatio = hasDocks ? dayCount / dockCount : 0;
                        const capacityClass = capRatio > 1 ? 'fc-capacity-over' : capRatio >= 0.7 ? 'fc-capacity-warn' : 'fc-capacity-ok';

                        return (
                            <div className={`fc-weekday-header ${isToday ? 'fc-weekday-today' : ''}`}>
                                <span className="fc-weekday-name">{dayName}</span>
                                <span className="fc-weekday-num">{dayNum}</span>
                                {hasDocks && dayCount > 0 && (
                                    <span className={`fc-header-capacity ${capacityClass}`}>
                                        {dayCount} / {dockCount}
                                    </span>
                                )}
                            </div>
                        );
                    }}
                    dayCellClassNames={(arg) => {
                        if (arg.isOther || !dockCount || dockCount <= 0) return [];
                        const iso = resolveCellIso(arg.date);
                        const count = dayApptCounts.get(iso) || 0;
                        const r = count / dockCount;
                        if (r > 1) return ['fc-day-overloaded'];
                        if (r >= 0.7) return ['fc-day-warn'];
                        return [];
                    }}
                    dayCellContent={(arg) => {
                        const iso = resolveCellIso(arg.date);
                        const count = arg.isOther ? 0 : (dayApptCounts.get(iso) || 0);
                        const hasDockData = dockCount && dockCount > 0;
                        const r = hasDockData ? count / dockCount : 0;
                        const level = r > 1 ? 'overloaded' : r >= 0.85 ? 'high' : r >= 0.5 ? 'medium' : 'low';
                        const densityClass = hasDockData
                            ? ({ low: 'fc-density-low', medium: 'fc-density-medium', high: 'fc-density-high', overloaded: 'fc-density-overloaded' } as Record<string, string>)[level]
                            : '';
                        const barColor = ({ low: '#94a3b8', medium: '#6366f1', high: '#f59e0b', overloaded: '#ef4444' } as Record<string, string>)[level];

                        return (
                            <div className="fc-daycell-custom" data-date-iso={iso}>
                                <div className="fc-daycell-header">
                                    <span className="fc-daycell-number">{arg.dayNumberText}</span>
                                    {!arg.isOther && count > 0 && (
                                        <span className={`fc-daycell-density ${densityClass}`} data-day-count={count}>
                                            {hasDockData ? (
                                                <>{count} / {dockCount}</>
                                            ) : (
                                                <>{count}</>
                                            )}
                                        </span>
                                    )}
                                </div>
                                {!arg.isOther && hasDockData && count > 0 && (
                                    <div className="fc-utilization-bar-track">
                                        <div
                                            className="fc-utilization-bar-fill"
                                            style={{
                                                width: `${Math.min(r * 100, 100)}%`,
                                                backgroundColor: barColor,
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    }}
                />
                
                {/* Month View Date Appointments Modal */}
                {selectedDateForModal && (() => {
                    const { startMs: dayStartMs, endMs: dayEndMs, dt } = getWarehouseDayRangeMs(selectedDateForModal, effectiveZone);

                    const dayAppts = appointments
                        .filter(a => {
                            const t = new Date(a.startAt).getTime();
                            return t >= dayStartMs && t < dayEndMs && normalizeStatus(a.status) !== 'cancelled';
                        })
                        .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

                    const titleDateStr = dt.toFormat('MMMM d, yyyy');

                    return (
                        <Dialog open={true} onOpenChange={(open) => !open && setSelectedDateForModal(null)}>
                            <DialogPortal>
                                <DialogOverlay className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50" />
                                <DialogContent className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-slate-900 rounded-xl shadow-xl w-[90vw] max-w-2xl max-h-[85vh] flex flex-col z-50 overflow-hidden border border-slate-200 dark:border-slate-800">
                                    <DialogHeader className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                                        <DialogTitle className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                                            Appointments for {titleDateStr}
                                        </DialogTitle>
                                    </DialogHeader>
                                    <div className="p-4 flex-1 overflow-y-auto">
                                        {dayAppts.length === 0 ? (
                                            <div className="text-center py-10 text-slate-500 dark:text-slate-400">
                                                No appointments scheduled for this day.
                                            </div>
                                        ) : (
                                            <div className="flex flex-col gap-2">
                                                {dayAppts.map(appt => {
                                                    const timeStr = formatEventTime(new Date(appt.startAt), effectiveZone);
                                                    const dockLabel = getDockLabel(appt);
                                                    const status = normalizeStatus(appt.status);
                                                    const isLate = status !== 'completed' && status !== 'cancelled'
                                                        && status !== 'checked_in' && status !== 'in_progress'
                                                        && new Date() > new Date(appt.startAt);
                                                    
                                                    return (
                                                        <div 
                                                            key={appt.id}
                                                            onClick={() => {
                                                                setSelectedDateForModal(null);
                                                                onEventClick(appt);
                                                            }}
                                                            className="flex items-center gap-4 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition-colors"
                                                        >
                                                            <div className="w-20 shrink-0 flex flex-col items-end">
                                                                <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-100 text-sm">
                                                                    {timeStr}
                                                                </span>
                                                                {isLate && (
                                                                    <span className="text-[10px] font-bold text-red-600 uppercase tracking-wide">
                                                                        Late
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="flex-1 min-w-0 flex flex-col">
                                                                <span className="font-semibold text-slate-800 dark:text-slate-200 truncate">
                                                                    {appt.carrierName || 'Unknown Carrier'}
                                                                </span>
                                                                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                                                    {(appt.customerPoNumber || appt.po_number) && (
                                                                        <span className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-slate-600 dark:text-slate-300 font-medium">
                                                                            PO: {appt.customerPoNumber || appt.po_number}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="shrink-0 flex flex-col items-end gap-1">
                                                                <span className="text-xs font-semibold px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded-full capitalize text-slate-600 dark:text-slate-400">
                                                                    {status.replace('_', ' ')}
                                                                </span>
                                                                {dockLabel && (
                                                                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                                                        {dockLabel}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex justify-end">
                                        <button 
                                            onClick={() => setSelectedDateForModal(null)}
                                            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-700"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </DialogContent>
                            </DialogPortal>
                        </Dialog>
                    );
                })()}
            </div>
        );
    }
);
